import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import _ from 'lodash';
import cx from 'classnames';

import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/services/file.service";
import { ErrorCodes } from "src/services/error.service";
import Tooltip from "src/components/tooltip";
import { Asset, AssetUpload } from "src/types/assets";
import { toast } from "react-toastify";
import Toast from "src/components/toast";

import styles from './add.module.scss';
import { Page } from "src/types/cleverstory";
import Text from "src/components/text";

interface AssetCardProps {
  asset: Asset;
  editable: boolean;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: Asset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, editable, onRemove }) => {

  const fileType = useMemo(() => {
    if (asset != null) {
      return FileService.getFileType(asset?.metadata);
    } else {
      return;
    }
  }, [asset]);

  const internal = !_.get(asset, 'settings.share', false);

  const status = _.get(asset, 'status');
  const optimizing = _.includes(['NEEDS_PROCESSING', 'PROCESSING'], status);
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  return (
    <div className={cx(styles.card)}>
      <div className={styles.icon}>
        <Thumbnail media={asset?.icon} defaultImage="/images/assets/default_asset.jpg" />
        {!optimizing &&
          <div className={styles.fileType}>{fileType}</div>
        }
        {optimizing &&
          <div className={styles.status}>Optimising...</div>
        }
        {internal &&
          <div className={styles.indicators}>
            <Tooltip content="Internal use only" placement="auto">
              <i className="paperflite-lock" />
            </Tooltip>
          </div>
        }
        <div className={styles.overlay}></div>
        {editable &&
          <button className={styles.remove} onClick={handleRemove}>
            <i className="fa-regular fa-circle-xmark"></i>
          </button>
        }
      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <Text text={asset?.name} tooltip={{placement:"bottom"}} />
        </div>
      </div>
    </div>
  );
}

interface AssetUploadCardProps {
  asset: AssetUpload;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: AssetUpload) => void;
}

const AssetUploadCard: React.FC<AssetUploadCardProps> = ({ asset, onRemove }) => {

  const [showTooltip, setShowTooltip] = React.useState(false);

  const error = _.get(asset, "error.errors[0]", { code: null, message: "" });

  useEffect(() => {
    if (_.get(error, "code") === ErrorCodes.STORAGE_LIMIT_REACHED) {
      toast(<Toast.warning heading="Warning" message={error.message} />, {
        position: "bottom-right",
        autoClose: false,
      });
    }
  }, [asset.error])
  

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  return (
    <div className={cx(styles.card, styles.upload)}>
      <div className={styles.icon}>
        <Thumbnail media={null} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.overlay}></div>
        <button className={styles.remove} onClick={handleRemove}>
          <i className="fa-regular fa-circle-xmark"></i>
        </button>
        {asset?.progress < 100 && !error?.code &&
          <div className={styles.progress}>
            <div className={styles.bar} style={{ width: `${asset?.progress}%` }}></div>
          </div>
        }

        {asset?.progress == 100 && !error?.code &&
          <div className={styles.status}>Processing...</div>
        }

        {error?.code &&
          <div className={styles.status}>ERROR</div>
        }

      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <Text text={asset?.name} tooltip={{placement:"bottom"}} />
        </div>
      </div>
    </div>
  );
}

interface LinkAssetCardProps {
  url: string | undefined,
  linkAsset?: AssetUpload;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: AssetUpload) => void;
}

const LinkAssetCard: React.FC<LinkAssetCardProps> = ({ url, linkAsset, onRemove }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const asset = useSelector((state: RootState) => _.get(state.asset, `linkAsset.${linkAsset?.id}`));
  const error = _.get(asset, 'error.errors[0]');

  const handleRemove = () => {
    if (onRemove && linkAsset) {
      onRemove(linkAsset);
    }
  }

  useEffect(() => {
    if (error?.code === ErrorCodes.STORAGE_LIMIT_REACHED) {
      toast(<Toast.warning heading="Warning" message={error?.message} />, {
        position: "bottom-right",
        autoClose: false,
      });
    }
  }, [asset?.error])

  return (
    <div className={cx(styles.card, styles.link, styles.placeholder)}>
      <div className={styles.icon}>
        <div className={styles.block}></div>
        <div className={styles.overlay}></div>
        <button className={styles.remove} onClick={handleRemove}>
          <i className="fa-regular fa-circle-xmark"></i>
        </button>
        <div className={styles.status}>
          {asset?.error ? 'ERROR' : 'Creating...'}
        </div>
      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <Text text={url} tooltip={{placement:"bottom"}} />
        </div>
      </div>
    </div>
  );
}

interface CleverstoryAssetCardProps {
  page: Page;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: AssetUpload) => void;
}

const CleverstoryAssetCard: React.FC<CleverstoryAssetCardProps> = ({ page }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const name = _.get(page, 'name', '');

  return (
    <div className={cx(styles.card, styles.link, styles.placeholder)}>
      <div className={styles.icon}>
        <div className={styles.block}></div>
        <div className={styles.overlay}></div>
        <div className={styles.status}>Creating...</div>
      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <Text text={name} tooltip={{placement:"bottom"}} />
        </div>
      </div>
    </div>
  );
}


export { AssetCard, AssetUploadCard, LinkAssetCard, CleverstoryAssetCard };